import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Calculator as CalcIcon, Fuel, ShieldCheck, CarFront, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { fetchVehicles, zar, mediaUrl } from "@/api";

const Slider = ({ label, min, max, step, value, onChange, format, testid }) => (
  <div>
    <div className="flex justify-between items-center mb-2">
      <label className="text-xs uppercase tracking-widest text-sky-300 font-bold" htmlFor={testid}>{label}</label>
      <span className="text-sm font-bold text-white" data-testid={`${testid}-value`}>{format(value)}</span>
    </div>
    <input
      id={testid}
      data-testid={testid}
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="w-full accent-[#F57C00]"
    />
  </div>
);

export default function Calculator() {
  const [vehicles, setVehicles] = useState([]);
  const [slug, setSlug] = useState("");
  const [deposit, setDeposit] = useState(10000);
  const [term, setTerm] = useState(48);
  const [km, setKm] = useState(1200);
  const [fuelPrice, setFuelPrice] = useState(23);
  const [consumption, setConsumption] = useState(7);
  const [insurance, setInsurance] = useState(0);

  useEffect(() => {
    fetchVehicles().then((vs) => {
      setVehicles(vs);
      if (vs.length) setSlug(vs[0].slug);
    }).catch(() => {});
  }, []);

  const vehicle = vehicles.find((v) => v.slug === slug);

  const calc = useMemo(() => {
    if (!vehicle) return null;
    const balance = Math.max(vehicle.price - deposit, 0);
    const ownership = balance / term;
    const rental = vehicle.day_rate * 30;
    const fuel = (km / 100) * consumption * fuelPrice;
    const total = ownership + rental + fuel + insurance;
    return { balance, ownership, rental, fuel, insurance, total };
  }, [vehicle, deposit, term, km, consumption, fuelPrice, insurance]);

  return (
    <div data-testid="calculator-page" className="pt-32 pb-24 min-h-screen">
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] text-sky-300 font-bold mb-4">True cost of ownership</p>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight font-black text-white mb-4">
            Know your real monthly number.
          </h1>
          <p className="text-base text-slate-400 leading-relaxed mb-12 max-w-2xl">
            Pick a vehicle, set your deposit and driving habits, and see the full monthly picture —
            ownership allocation, rental &amp; service charge, fuel and insurance. No surprises later.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          <Reveal delay={0.1}>
            <div className="bg-[#122659]/80 backdrop-blur-xl border border-sky-300/20 rounded-xl p-8 space-y-7">
              <div>
                <label className="text-xs uppercase tracking-widest text-sky-300 font-bold block mb-2" htmlFor="calc-vehicle">Vehicle</label>
                <select id="calc-vehicle" data-testid="calc-vehicle-select" className="input-field" value={slug} onChange={(e) => setSlug(e.target.value)}>
                  {vehicles.map((v) => (
                    <option key={v.slug} value={v.slug}>{v.make} {v.model} ({v.year}) — {zar(v.price)}</option>
                  ))}
                </select>
              </div>
              <Slider label="Ownership deposit" min={0} max={vehicle ? Math.min(vehicle.price * 0.4, 80000) : 80000} step={1000} value={deposit} onChange={setDeposit} format={zar} testid="calc-deposit" />
              <div>
                <p className="text-xs uppercase tracking-widest text-sky-300 font-bold mb-2">Term</p>
                <div className="flex gap-2" data-testid="calc-term-toggle">
                  {[48, 60].map((m) => (
                    <button key={m} data-testid={`calc-term-${m}`} onClick={() => setTerm(m)} className={`flex-1 text-sm font-bold px-4 py-3 rounded-md transition-colors duration-200 ${term === m ? "bg-[#F57C00] text-white" : "bg-[#0B1F4E] text-slate-400 hover:text-white"}`}>
                      {m} months
                    </button>
                  ))}
                </div>
              </div>
              <Slider label="Kilometres per month" min={200} max={4000} step={100} value={km} onChange={setKm} format={(n) => `${n.toLocaleString()} km`} testid="calc-km" />
              <Slider label="Fuel price (per litre)" min={15} max={35} step={0.5} value={fuelPrice} onChange={setFuelPrice} format={(n) => `R${n.toFixed(2)}`} testid="calc-fuel-price" />
              <Slider label="Consumption (L / 100km)" min={4} max={14} step={0.1} value={consumption} onChange={setConsumption} format={(n) => `${n.toFixed(1)} L`} testid="calc-consumption" />
              <Slider label="Insurance budget (optional)" min={0} max={3000} step={100} value={insurance} onChange={setInsurance} format={zar} testid="calc-insurance" />
            </div>
          </Reveal>

          <Reveal delay={0.2}>
            <div className="bg-[#122659] border border-sky-300/20 rounded-xl overflow-hidden shadow-2xl lg:sticky lg:top-28" data-testid="calc-result-card">
              {vehicle && (
                <div className="flex items-center gap-4 px-8 py-6 border-b border-sky-300/10">
                  {vehicle.image && <img src={mediaUrl(vehicle.image)} alt="" className="w-20 h-14 object-cover rounded-md border border-sky-300/20" />}
                  <div>
                    <p className="font-bold text-white" data-testid="calc-vehicle-name">{vehicle.make} {vehicle.model}</p>
                    <p className="text-xs text-slate-500">{vehicle.year} · {vehicle.transmission} · {vehicle.fuel} · Cash price {zar(vehicle.price)}</p>
                  </div>
                </div>
              )}
              {calc && (
                <div className="px-8 py-6 space-y-4">
                  {[
                    [`Ownership allocation / month (${term} m)`, zar(calc.ownership), CalcIcon],
                    [`Rental & service / month (R${vehicle.day_rate}/day × 30)`, zar(calc.rental), CarFront],
                    [`Estimated fuel / month (${km.toLocaleString()} km)`, zar(calc.fuel), Fuel],
                    ["Insurance budget / month", zar(calc.insurance), ShieldCheck],
                  ].map(([label, value, Icon]) => (
                    <div key={label} className="flex justify-between items-center text-sm border-b border-white/5 pb-3">
                      <span className="text-slate-400 flex items-center gap-2"><Icon size={14} className="text-sky-300 shrink-0" />{label}</span>
                      <span className="font-semibold text-white whitespace-nowrap pl-3">{value}</span>
                    </div>
                  ))}
                  <div className="flex justify-between items-center pt-2">
                    <span className="text-sm font-bold text-sky-300 uppercase tracking-wider">Total monthly</span>
                    <span className="font-display text-3xl font-black text-[#F57C00]" data-testid="calc-total">{zar(calc.total)}</span>
                  </div>
                  <p className="text-xs text-slate-500">
                    Of that, <strong className="text-slate-300">{zar(calc.ownership)}</strong> builds your ownership every month — after {term} payments the vehicle is yours.
                  </p>
                </div>
              )}
              <div className="px-8 py-6 border-t border-sky-300/10 bg-[#0B1F4E]/50">
                <Link
                  to={vehicle ? `/apply?vehicle=${vehicle.slug}` : "/apply"}
                  data-testid="calc-apply-btn"
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#F57C00] hover:bg-[#E67300] text-white font-bold px-6 py-4 rounded-md transition-colors duration-300"
                >
                  Apply for this vehicle <ArrowRight size={16} />
                </Link>
                <p className="text-[11px] text-slate-500 leading-relaxed mt-4">
                  Illustrative estimate only. Insurance included in the rental &amp; service charge per agreement;
                  the insurance budget above is for any extra cover you choose. Final quotation is vehicle-specific
                  and provided in writing before signature.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  );
}
